import {
  ADD_LINE, APPLY_DELIVERY_DEPOSIT,
  APPLY_INVOICE_FEE,
  APPLY_PROMO,
  ApplyInvoiceFee,
  ApplyPromo,
  CANCEL_INVOICE_FEE,
  CANCEL_PROMO,
  CartAction,
  DELIVERY_INFO_UPDATE,
  DeliveryInfoUpdate,
  LOAD_CART,
  LOAD_CART_FAIL,
  LOAD_CART_SUCCESS,
  LoadCartSuccess,
  REMOVE_LINE,
  RESET_CART,
  SET_BAKERY,
  SET_DELIVERY,
  SET_DELIVERY_ACTIVE_STATUS,
  SetBakery,
  SetDelivery,
  SetDeliveryActiveStatus,
  UPDATE_LINE,
} from '../cart.actions';
import { ICart } from '../../models/cart/i-cart';
import { linesReducer } from './cart-lines.reducer';
import { cartHelper, getActualDeliveryPrice } from '../../helpers/cart-helper';
import { DISCOUNT_TYPES } from '../../constants/common';

export interface CartState {
  cart: ICart;
  loaded: boolean;
  loading: boolean;
  error?: boolean;
}

export const initialCartState: CartState = {
  cart: null,
  loaded: false,
  loading: false,
};

export function cartReducer(
  state = initialCartState,
  action: CartAction
): CartState {
  switch (action.type) {
    case LOAD_CART: {
      return {
        ...state,
        loading: true,
      };
    }

    case LOAD_CART_SUCCESS: {
      return {
        cart: {
          ...(action as LoadCartSuccess).payload,
          invoiceFee: state.cart ? state.cart.invoiceFee : null,
          prevAppliedDiscount: state.cart ? state.cart.prevAppliedDiscount : null,
          isDeliveryActive: state.cart && state.cart.isDeliveryActive !== undefined ? state.cart.isDeliveryActive : true,
          additionalDeliveryInfo: state.cart ? state.cart.additionalDeliveryInfo : null,
        },
        loading: false,
        loaded: true
      };
    }

    case LOAD_CART_FAIL: {
      return {
        ...state,
        loading: false,
        loaded: false,
        error: true
      };
    }

    case ADD_LINE:
    case REMOVE_LINE:
    case UPDATE_LINE:
      const lines = linesReducer(state.cart.lines, action, state.cart);

      // clean delivery if there is no order lines anymore
      const delivery = lines.length > 0
        ? {
          ...state.cart.delivery,
          price: cartHelper.getDeliveryPriceAfterLinesUpdate(state.cart, lines)
        }
        : null;

      return {
        ...state,
        cart: cartHelper.getCartStateWithUpdatedPrices({
          ...state.cart,
          delivery,
          lines
        }),
      };

    case DELIVERY_INFO_UPDATE: {
      const {
        delivery,
        freeDeliveryEnabled,
        freeDeliveryAllowsOnlyForCompanies,
        minCartPriceAllowsFreeDelivery,
        customDeliveryPriceEnabled,
        deliveryPrice
      } = cartHelper.prepareDataFromDeliveryInfoUpdatePayload((action as DeliveryInfoUpdate).payload, state.cart);

      return {
        ...state,
        cart: cartHelper.getCartStateWithUpdatedPrices({
          ...state.cart,
          delivery,
          additionalDeliveryInfo: {
            ...state.cart.additionalDeliveryInfo,
            freeDeliveryEnabled,
            freeDeliveryAllowsOnlyForCompanies,
            minCartPriceAllowsFreeDelivery,
            customDeliveryPriceEnabled,
            deliveryPrice,
          }
        })
      };
    }

    case APPLY_PROMO: {
      let { appliedDiscount } = (action as ApplyPromo);
      const { COMPANY_DISCOUNT, BAKERY_DISCOUNT, PROMO_CODE } = DISCOUNT_TYPES;
      const currentDiscount = state.cart.appliedDiscount;
      const personalDiscount = state.cart.prevAppliedDiscount;

      /**If promo code or bakery discount is applied, company discount shouldn't be applied*/
      if (currentDiscount && (currentDiscount.type === BAKERY_DISCOUNT || currentDiscount.type === PROMO_CODE)) {
        return { ...state };
      }

      if (currentDiscount && appliedDiscount.type === COMPANY_DISCOUNT) {
        // if we get updated company promo, but we have already bigger personal, do nothing
        if (currentDiscount.type !== appliedDiscount.type && currentDiscount.value > appliedDiscount.value) {
          return { ...state };
        }

        // if we get updated company promo, but in the beginning we had a bigger personal, switch to personal
        if (currentDiscount.type === appliedDiscount.type && personalDiscount
          && personalDiscount.value > appliedDiscount.value) {
          appliedDiscount = personalDiscount;
        }
      }

      const prevAppliedDiscount = state.cart.prevAppliedDiscount
        || (state.cart.appliedDiscount
        && state.cart.appliedDiscount.type === COMPANY_DISCOUNT ? null : state.cart.appliedDiscount);

      return {
        ...state,
        cart: cartHelper.getCartStateWithUpdatedPrices({
          ...state.cart,
          prevAppliedDiscount,
          appliedDiscount
        })
      };
    }

    case CANCEL_PROMO: {
      const prevAppliedDiscount = state.cart.prevAppliedDiscount;
      const returnPrevDiscount = state.cart.appliedDiscount
        && state.cart.appliedDiscount.type === DISCOUNT_TYPES.COMPANY_DISCOUNT;

      if (!state.cart.appliedDiscount || !returnPrevDiscount) {
        return state;
      }

      return {
        ...state,
        cart: cartHelper.getCartStateWithUpdatedPrices({
          ...state.cart,
          prevAppliedDiscount: null,
          appliedDiscount: prevAppliedDiscount || null,
        })
      };
    }

    case APPLY_INVOICE_FEE: {
      return {
        ...state, cart: cartHelper.getCartStateWithUpdatedPrices({
          ...state.cart, invoiceFee: (action as ApplyInvoiceFee).payload
        })
      };
    }

    case CANCEL_INVOICE_FEE: {
      return {
        ...state, cart: cartHelper.getCartStateWithUpdatedPrices({
          ...state.cart, invoiceFee: null
        })
      };
    }

    case APPLY_DELIVERY_DEPOSIT: {
      const { cart: { delivery, bakery: { deliveryDeposit } } } = state;
      return {
        ...state, cart: cartHelper.getCartStateWithUpdatedPrices({
          ...state.cart, delivery: { ...delivery, price: deliveryDeposit }
        })
      };
    }

    case SET_DELIVERY_ACTIVE_STATUS: {
      const isDeliveryActive = (action as SetDeliveryActiveStatus).payload;
      const cart = cartHelper.getCartStateWithUpdatedPrices(state.cart);

      let { totalPrice } = cart;

      if (!isDeliveryActive) {
        const deliveryPrice = cart.delivery && getActualDeliveryPrice(cart.delivery) || 0;
        totalPrice = totalPrice - deliveryPrice;
      }

      return {
        ...state,
        cart: {
          ...cart,
          isDeliveryActive,
          totalPrice,
        }
      };
    }

    case SET_BAKERY: {
      return {
        ...state,
        cart: {
          ...state.cart,
          bakery: (action as SetBakery).payload
        }
      };
    }

    case SET_DELIVERY: {
      return {
        ...state,
        cart: {
          ...state.cart,
          delivery: (action as SetDelivery).payload
        }
      };
    }

    case RESET_CART: {
      return {
        cart: { id: null, lines: [] } as any,
        loading: false,
        loaded: true
      };
    }

    default:
      return state;
  }
}
